import React, { useEffect, useState } from 'react';
import API from '../../api/api';
import { MediaItem } from '../../api/structs/media';
import { MediaOutputSpec } from '../../api/structs/media-spec';
import { Button, TextArea } from '../ui';
import CheckpointProviderSelector from '../selectors/CheckpointProviderSelector';
import ModelSettingsSection from '../selectors/ModelSettingsSection';
import { useToast } from '../../hooks/useToast';
import ModelsAPI from '../../api/models';
import { AIModel, usdToCredits } from '../../api/structs/model';
import { fetchVideoCapabilities } from '../../api/fetchVideoCapabilities';
import { readRequestError } from '../../api/requestError';
import {
  UNKNOWN_VIDEO_CAPABILITIES,
  VideoCapabilities,
  supportsLastFrameOf,
} from '../../api/videoCapabilities';
import { useCostDisplay } from '../../hooks/useCostDisplay';
import ReferenceFramesEditor from '../clips/sections/ReferenceFramesEditor';
import {
  ReferenceFrameState,
  buildReferenceFramesPayload,
  isFrameSlotDirty,
  isReferenceFrameStateDirty,
  metadataSyncForFrames,
  readReferenceFrames,
} from '../clips/sections/referenceFrames';
import {
  ReferenceInputState,
  buildReferenceInputsPayload,
  emptyReferenceInputs,
  isReferenceInputStateDirty,
  readReferenceInputs,
  withoutUnsupportedReferenceLists,
} from '../clips/sections/referenceInputs';
import { RegenerateOutputSpec } from '../../api/media';

/** Per-model frame-image support, surfaced to hosts via `onFrameCapsChange`. */
export interface FrameCapabilities {
  known: boolean;
  /** The model's parsed video capabilities — defaults (all off) while unknown. */
  caps: VideoCapabilities;
}

interface RegenerateFormProps {
  item: MediaItem;
  outputSpec?: MediaOutputSpec;
  /** Stills from the same scene, offered as reference-frame bindings. */
  sceneImages?: MediaItem[];
  /** Submit button text; defaults to "Regenerate". */
  submitLabel?: string;
  /**
   * Controlled reference-frame state (video items). When both are given the
   * host owns the bindings — the scene-level modal uses this to bind a freshly
   * regenerated frame into the video's payload from OUTSIDE the form. Left
   * unset, the form keeps its own state exactly as the classic modal did.
   */
  frames?: ReferenceFrameState;
  onFramesChange?: (next: ReferenceFrameState) => void;
  /**
   * Controlled reference-INPUT state, same contract as `frames` above: both
   * halves or neither. The scene-level modal owns these so the fallback frames
   * editor it mounts separately edits the very same references this form
   * submits.
   */
  references?: ReferenceInputState;
  onReferencesChange?: (next: ReferenceInputState) => void;
  /** Reports resolved model frame capabilities so hosts can gate their own UI. */
  onFrameCapsChange?: (caps: FrameCapabilities) => void;
  /** Rendered between the provider section and the frames editor. */
  banner?: React.ReactNode;
  /** Called after a regeneration was successfully started. */
  onSubmitted: () => void;
  /** Renders a Cancel button when provided; hosts with their own close affordance omit it. */
  onCancel?: () => void;
}

const toSelectorModality = (type: MediaItem['type']): 'image' | 'video' | 'audio' => {
  if (type === 'ai_video') return 'video';
  if (type === 'audio') return 'audio';
  return 'image';
};

/**
 * The toast line for a refused request, preferring the backend's own words.
 *
 * A reference-input refusal comes back as 422 with the rule it broke in the
 * body (`{"error": …, "code": "reference_inputs_invalid"}` — the very shape the
 * Generate tab renders inline); an axios `.message` for that reads "Request
 * failed with status code 422", which names nothing the operator can change.
 * This surface is the one MOST likely to hit that refusal, because it carries
 * the item's stored reference lists forward.
 */
const failureToastText = (error: any): string =>
  `Failed: ${readRequestError(error, String(error?.message ?? 'Unknown error'))}`;

/**
 * The regeneration form itself — prompt, provider/model, reference frames,
 * advanced settings, submit. Extracted from `RegenerateModal` so the
 * scene-level modal can mount one form per tab (video / opening frame /
 * closing frame) without re-wiring the PUT skip_regeneration → POST regenerate
 * sequence three times. `RegenerateModal` stays the thin single-item wrapper.
 */
const RegenerateForm: React.FC<RegenerateFormProps> = ({
  item,
  outputSpec,
  sceneImages = [],
  submitLabel = 'Regenerate',
  frames: controlledFrames,
  onFramesChange,
  references: controlledReferences,
  onReferencesChange,
  onFrameCapsChange,
  banner,
  onSubmitted,
  onCancel,
}) => {
  const toast = useToast();
  const display = useCostDisplay();
  const resolvedSpec = item.output_spec ?? outputSpec;
  const [prompt, setPrompt] = useState(item.prompt);
  const [provider, setProvider] = useState(resolvedSpec?.provider ?? '');
  const [model, setModel] = useState(resolvedSpec?.model ?? '');
  const [settings, setSettings] = useState<Partial<MediaOutputSpec>>(resolvedSpec ?? {});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [estimatedCredits, setEstimatedCredits] = useState<number | null>(null);
  const isVideo = item.type === 'ai_video';
  const [initialFrames, setInitialFrames] = useState<ReferenceFrameState>(() =>
    readReferenceFrames(item)
  );
  const [internalFrames, setInternalFrames] = useState<ReferenceFrameState>(initialFrames);
  // Controlled when the host passes both halves; otherwise self-owned.
  const framesControlled = controlledFrames !== undefined && !!onFramesChange;
  const frames = framesControlled ? (controlledFrames as ReferenceFrameState) : internalFrames;
  const setFrames = framesControlled ? onFramesChange! : setInternalFrames;
  const [initialReferences, setInitialReferences] = useState<ReferenceInputState>(() =>
    readReferenceInputs(item)
  );
  const [internalReferences, setInternalReferences] =
    useState<ReferenceInputState>(initialReferences);
  const referencesControlled = controlledReferences !== undefined && !!onReferencesChange;
  const references = referencesControlled
    ? (controlledReferences as ReferenceInputState)
    : internalReferences;
  const setReferences = referencesControlled ? onReferencesChange! : setInternalReferences;
  const [frameCaps, setFrameCaps] = useState<FrameCapabilities>({
    known: false,
    caps: UNKNOWN_VIDEO_CAPABILITIES,
  });
  const [regeneratingImageId, setRegeneratingImageId] = useState<string | null>(null);
  const [frameRefreshToken, setFrameRefreshToken] = useState(0);

  const effectiveProvider = provider || resolvedSpec?.provider || '';
  const effectiveModel = model || resolvedSpec?.model || '';
  const effectiveDuration =
    item.type === 'ai_video'
      ? (settings.duration ?? resolvedSpec?.duration ?? (item.metadata?.duration as number) ?? 5)
      : undefined;

  // Keyed on the media ID, not the item object: the scene-level modal hands in
  // the LIVE item, which becomes a NEW object on every clip refetch (e.g. a
  // sibling frame's regeneration completing mid-edit). Resetting on identity
  // would wipe the user's in-progress prompt/provider/frame edits; only an
  // actually different media item warrants a reset.
  useEffect(() => {
    setPrompt(item.prompt);
    setProvider(resolvedSpec?.provider ?? '');
    setModel(resolvedSpec?.model ?? '');
    setSettings(resolvedSpec ?? {});
    const bound = readReferenceFrames(item);
    setInitialFrames(bound);
    if (!framesControlled) setInternalFrames(bound);
    const boundReferences = readReferenceInputs(item);
    setInitialReferences(boundReferences);
    if (!referencesControlled) setInternalReferences(boundReferences);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  // Frame-image support is per-model: the registry exposes it as the video
  // capability `supports_frame_images` plus `supported_frame_positions`. Without
  // it the provider rejects frameImages, so the closing-frame controls stay hidden.
  useEffect(() => {
    if (!isVideo || !effectiveModel) {
      const caps: FrameCapabilities = { known: !isVideo, caps: UNKNOWN_VIDEO_CAPABILITIES };
      setFrameCaps(caps);
      onFrameCapsChange?.(caps);
      return;
    }
    let cancelled = false;
    // `known` comes STRAIGHT from the fetch result, and a registry failure
    // reports false. This form used to stamp a failure as `{ known: true, all
    // off }` — a lie that was merely a display bug while `known` only hid
    // controls, and became a destructive one once it started gating the drop of
    // stored reference lists below: an outage would have answered "this model
    // declares no references" and emptied them.
    void fetchVideoCapabilities(effectiveModel).then((result) => {
      if (cancelled) return;
      const caps: FrameCapabilities = { known: result.known, caps: result.caps };
      setFrameCaps(caps);
      onFrameCapsChange?.(caps);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveModel, isVideo]);

  useEffect(() => {
    let cancelled = false;
    if (!effectiveModel) {
      setEstimatedCredits(null);
      return;
    }

    const fetchEstimate = async () => {
      const list: AIModel[] = await (
        item.type === 'ai_video'
          ? ModelsAPI.getVideoModels(effectiveProvider || undefined)
          : item.type === 'audio'
          ? ModelsAPI.getAudioModels(effectiveProvider || undefined)
          : ModelsAPI.getImageModels(effectiveProvider || undefined)
      ).catch(() => []);
      const selected = list.find((entry) => entry.id === effectiveModel);
      if (!selected?.pricing) {
        if (!cancelled) setEstimatedCredits(null);
        return;
      }
      if (selected.pricing.per_image) {
        if (!cancelled) setEstimatedCredits(usdToCredits(parseFloat(selected.pricing.per_image)));
        return;
      }
      if (selected.pricing.per_second) {
        const seconds = effectiveDuration ?? 5;
        if (!cancelled) {
          setEstimatedCredits(usdToCredits(parseFloat(selected.pricing.per_second) * seconds));
        }
        return;
      }
      if (!cancelled) setEstimatedCredits(null);
    };

    void fetchEstimate();
    return () => {
      cancelled = true;
    };
  }, [effectiveDuration, effectiveModel, effectiveProvider, item.type]);

  /**
   * A new model is a new context — the same rule the Generate tab's `[model]`
   * effect applies, and for the same reason: a reference list is capped by THIS
   * model's `max_reference_images`, so three picks made against a cap-3 model
   * sitting in a cap-1 model's form is a request the form already knows is over
   * cap. On a model that takes no references at all the editor renders nothing
   * while the list is still submitted — invisible state on the wire.
   *
   * Written as a handler rather than an effect on `effectiveModel` so it cannot
   * fire on mount and wipe the bindings the item was saved with. FRAMES are
   * deliberately untouched, exactly as on the Generate tab.
   */
  const handleModelReplaced = () => {
    setSettings({});
    setReferences(emptyReferenceInputs());
  };

  const handleRegenerateReferenceImage = async (image: MediaItem) => {
    setRegeneratingImageId(image.id);
    try {
      await API.regenerateMedia(image.id);
      toast({ text: 'Reference image regeneration started', level: 'success' });
      // The regenerate overwrites the same media id in place, so the binding
      // still points at the right still — only the painted bytes change.
      setFrameRefreshToken((token) => token + 1);
    } catch (error: any) {
      toast({ text: failureToastText(error), level: 'error' });
    } finally {
      setRegeneratingImageId(null);
    }
  };

  /**
   * Nothing about the frames may be written until the model's frame
   * capabilities are KNOWN.
   *
   * `supportsLastFrame` is false while the constraints fetch is in flight, and
   * both the payload builder and the metadata sync read that as "this model has
   * no closing frame". A submit racing the fetch would therefore unchain a
   * perfectly good closing frame — and since the scene dialog now refreshes a
   * regenerated slot on its own, "dirty" no longer implies the operator did
   * anything, so that race is reachable without a single click. An unknown
   * capability sends no frame payload at all and the item's stored frames stand.
   */
  const framesChanged =
    isVideo && frameCaps.known && isReferenceFrameStateDirty(initialFrames, frames);
  const supportsLastFrame = supportsLastFrameOf(frameCaps.caps);
  // Same gate, same reason: until the model's capabilities are known the
  // editor offers no reference rows, so "dirty" could only come from a race.
  const referencesChanged =
    isVideo && frameCaps.known && isReferenceInputStateDirty(initialReferences, references);

  const handleRegenerate = async () => {
    setIsSubmitting(true);
    try {
      // First save the prompt without triggering regeneration
      await API.editMediaItem(item.id, {
        new_prompt_string: prompt,
        skip_regeneration: true,
      });

      // Then trigger explicit regeneration with the chosen provider/model
      const spec: RegenerateOutputSpec = {
        ...(resolvedSpec || {}),
        ...settings,
        provider: provider || resolvedSpec?.provider || '',
        model: model || resolvedSpec?.model || '',
        ...(item.type === 'ai_video' ? { duration: effectiveDuration } : {}),
      };

      if (framesChanged) {
        // Sent whenever the bindings moved, EMPTY LIST INCLUDED. The key being
        // present is what makes it authoritative: an absent key leaves the
        // item's stored frames alone, so omitting an emptied list (as this used
        // to) left a frame the operator had CLEARED silently bound, and the
        // next generation opened on it anyway.
        const referenceFrames = buildReferenceFramesPayload(frames, supportsLastFrame);
        spec.ai_video = { ...(spec.ai_video ?? {}), reference_frames: referenceFrames };
        // The runner appends the chained closing frame from metadata regardless
        // of `reference_frames` (requestBuilder.go:374), so unchaining has to
        // clear the stamped URL too — otherwise the old frame comes right back.
        //
        // Written PER SLOT, though: a slot nobody moved keeps its stamped
        // values. The scene dialog's auto-refresh of the OPENING slot makes the
        // whole state dirty, and a blanket sync would then blank
        // `last_reference_image_url` / `last_reference_media_id` and unchain a
        // closing frame the operator never went near.
        const metadata = metadataSyncForFrames(frames, supportsLastFrame);
        const touched = {
          first: isFrameSlotDirty(initialFrames, frames, 'first'),
          last: isFrameSlotDirty(initialFrames, frames, 'last'),
        };
        await Promise.all(
          Object.entries(metadata)
            .filter(([key]) => (key.startsWith('last_') ? touched.last : touched.first))
            .map(([key, value]) => API.editMediaMetadata(item.id, key, value))
        );
      }

      if (referencesChanged) {
        // Present-beats-non-empty, exactly as for reference_frames: the
        // backend's mergeVideoReferenceLists replaces a list whenever the
        // override carries one, so an emptied list is how a slot is CLEARED
        // and has to be sent rather than omitted.
        spec.ai_video = { ...(spec.ai_video ?? {}), ...buildReferenceInputsPayload(references) };
      } else if (isVideo && frameCaps.known && spec.ai_video) {
        // `frameCaps.known` is the REGISTRY having answered, not merely the
        // fetch having settled — see the effect above. Without that
        // distinction an outage reads as "declares nothing" and this branch
        // empties lists the operator never touched.
        // Nothing was edited, but `spec` is built off the ITEM's stored
        // output_spec, so a list saved against the previous model rides along
        // to the newly chosen one whether or not that model can take it. It is
        // never shown (the editor offers no row for an undeclared kind), so the
        // operator's first news of it would be a 422. Dropped by sending it
        // EXPLICITLY EMPTY rather than by omitting the key: the same
        // present-beats-non-empty rule as above — an omitted key would leave
        // the stored list bound.
        spec.ai_video = withoutUnsupportedReferenceLists(spec.ai_video, frameCaps.caps);
      }

      await API.regenerateMedia(item.id, { output_spec: spec });
      toast({ text: 'Regeneration started', level: 'success' });
      onSubmitted();
    } catch (error: any) {
      toast({ text: failureToastText(error), level: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <TextArea
        label="Prompt"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={4}
      />

      <div className="space-y-2 pt-2 border-t border-white/10">
        <p className="text-xs uppercase tracking-[0.15em] text-zinc-300 font-medium">Provider / Model</p>
        <CheckpointProviderSelector
          provider={provider}
          model={model}
          modality={toSelectorModality(item.type)}
          onProviderChange={(value) => {
            setProvider(value);
            setModel('');
            handleModelReplaced();
          }}
          onModelChange={(value) => {
            setModel(value);
            handleModelReplaced();
          }}
        />
      </div>

      {banner}

      {isVideo && (
        <ReferenceFramesEditor
          state={frames}
          onChange={setFrames}
          candidates={sceneImages}
          caps={frameCaps.caps}
          capabilityKnown={frameCaps.known}
          onRegenerateImage={handleRegenerateReferenceImage}
          busyMediaId={regeneratingImageId}
          refreshToken={frameRefreshToken}
          references={references}
          onReferencesChange={setReferences}
        />
      )}

      <ModelSettingsSection
        modelId={model || resolvedSpec?.model || ''}
        modality={toSelectorModality(item.type)}
        settings={settings}
        onSettingsChange={setSettings}
        title="Advanced settings"
        description={
          item.type === 'ai_video'
            ? 'Override inherited duration, width, height and advanced model fields.'
            : 'Override inherited width, height and advanced model fields.'
        }
      />

      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>Cancel</Button>
        )}
        <Button onClick={handleRegenerate} disabled={isSubmitting}>
          <span className="flex items-center gap-1.5">
            {isSubmitting ? 'Starting...' : submitLabel}
            {!isSubmitting && display === 'credits' && estimatedCredits != null && (
              <span className="text-yellow-300 font-bold">⚡ ~{estimatedCredits.toLocaleString()}</span>
            )}
          </span>
        </Button>
      </div>
    </div>
  );
};

export default RegenerateForm;
